/**
 * 録音制御・無音検知フック
 * interview.tsxの録音ロジックを抽出
 */

import { useState, useRef, useEffect } from "react";
import { WebSocketClient, postClientEvent } from "../lib/websocket";
import { toUserFacingError } from "../lib/user-facing-error";

// 無音検知のデフォルト設定
const DEFAULT_SILENCE_THRESHOLD_DB = -35; // dB
const SILENCE_DURATION = 2000; // ミリ秒
const VOLUME_CHECK_INTERVAL = 100; // ミリ秒
const RMS_THRESHOLD = 0.01;
const MEDIA_RECORDER_TIMESLICE_MS = 80;
const PRE_READY_AUDIO_BUFFER_MAX_BYTES = 5 * 1024 * 1024;
const PRE_READY_AUDIO_BUFFER_MAX_CHUNKS = Math.ceil(10_000 / MEDIA_RECORDER_TIMESLICE_MS);
const RECORDING_START_TIMEOUT_MS = 10_000;
const RECOGNITION_READY_TIMEOUT_MS = 5_000;
const AUDIO_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/aac',
] as const;

type AudioRecorderEventName =
  | 'prepare_start'
  | 'ws_connect_start'
  | 'ws_connect_success'
  | 'ws_connect_failed'
  | 'session_ready_start'
  | 'session_ready_success'
  | 'session_ready_failed'
  | 'get_user_media_start'
  | 'get_user_media_success'
  | 'media_recorder_create_start'
  | 'media_recorder_created'
  | 'recognition_start_sent'
  | 'recognition_ready_wait_start'
  | 'recognition_ready'
  | 'recognition_ready_failed'
  | 'media_recorder_start'
  | 'ready'
  | 'recording_stopped'
  | 'silent_audio_rejected'
  | 'timeout'
  | 'error'
  | 'cancelled'
  | 'audio_context_error';

export type AudioStopReason = 'user' | 'silence' | 'session_missing';

interface SelectedMediaRecorderOptions {
  options?: MediaRecorderOptions;
  requestedMimeType: string | null;
  logMimeType: string;
}

function selectMediaRecorderOptions(): SelectedMediaRecorderOptions {
  if (typeof MediaRecorder === 'undefined') {
    return {
      requestedMimeType: null,
      logMimeType: 'media-recorder-unavailable',
    };
  }

  const canProbeMime = typeof MediaRecorder.isTypeSupported === 'function';
  for (const mimeType of AUDIO_MIME_CANDIDATES) {
    if (!canProbeMime || MediaRecorder.isTypeSupported(mimeType)) {
      return {
        options: { mimeType },
        requestedMimeType: mimeType,
        logMimeType: mimeType,
      };
    }
  }

  return {
    requestedMimeType: null,
    logMimeType: 'browser-default',
  };
}

function createMediaRecorder(stream: MediaStream): {
  mediaRecorder: MediaRecorder;
  requestedMimeType: string | null;
  actualMimeType: string;
  usedBrowserDefaultFallback: boolean;
} {
  const selected = selectMediaRecorderOptions();

  try {
    const mediaRecorder = selected.options
      ? new MediaRecorder(stream, selected.options)
      : new MediaRecorder(stream);
    return {
      mediaRecorder,
      requestedMimeType: selected.requestedMimeType,
      actualMimeType: mediaRecorder.mimeType || selected.logMimeType,
      usedBrowserDefaultFallback: false,
    };
  } catch (error) {
    if (!selected.options) throw error;

    debugWarn('[Recording] 選択したMIMEでMediaRecorderを作成できません。ブラウザ既定にフォールバックします。', {
      mimeType: selected.requestedMimeType,
      error,
    });
    const mediaRecorder = new MediaRecorder(stream);
    return {
      mediaRecorder,
      requestedMimeType: selected.requestedMimeType,
      actualMimeType: mediaRecorder.mimeType || 'browser-default',
      usedBrowserDefaultFallback: true,
    };
  }
}

function isAudioRecordingDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;

  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("audioDebug") === "1" || window.localStorage.getItem("dentalAvatarAudioDebug") === "1";
  } catch {
    return false;
  }
}

function debugLog(...args: unknown[]): void {
  if (isAudioRecordingDebugEnabled()) {
    console.log(...args);
  }
}

function debugWarn(...args: unknown[]): void {
  if (isAudioRecordingDebugEnabled()) {
    console.warn(...args);
  }
}

function errorToPayload(error: unknown): { errorName: string; errorMessage: string } {
  if (error instanceof Error) {
    return {
      errorName: error.name || 'Error',
      errorMessage: error.message,
    };
  }

  return {
    errorName: 'UnknownError',
    errorMessage: String(error),
  };
}

export interface AudioRecordingCallbacks {
  toast: (opts: { title: string; description: string; variant?: "default" | "destructive" | null | undefined }) => void;
  setIsAwaitingResponse: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface SharedAudioRefs {
  wsClientRef: React.MutableRefObject<WebSocketClient | null>;
  sessionIdRef: React.MutableRefObject<string | null>;
  recognitionClientIdRef: React.MutableRefObject<string | null>;
  sttSessionMessageIdRef: React.MutableRefObject<string | null>;
  sttPreviousInterimTextRef: React.MutableRefObject<string>;
  sttConfirmedTextRef: React.MutableRefObject<string>;
  /** G(STT readyゲート): STT セッション確立確認まで音声バイナリ送信を保留する共有フラグ。 */
  sttReadyRef: React.MutableRefObject<boolean>;
}

export interface AudioRecordingOptions {
  silenceThresholdDb?: number; // dB（デフォルト: -35）
}

export function useAudioRecording(
  sharedRefs: SharedAudioRefs,
  callbacks: AudioRecordingCallbacks,
  options?: AudioRecordingOptions,
) {
  const silenceThreshold = options?.silenceThresholdDb ?? DEFAULT_SILENCE_THRESHOLD_DB;
  const [isRecording, setIsRecording] = useState(false);
  const [isRecorderReady, setIsRecorderReady] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  const isRecordingRef = useRef<boolean>(false);
  const isStoppingRef = useRef<boolean>(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserNodeRef = useRef<AnalyserNode | null>(null);
  const silenceDetectionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSoundTimeRef = useRef<number>(0);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout>();
  const recordingStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recordingStartGenerationRef = useRef<number>(0);
  const recognitionStartedRef = useRef<boolean>(false);
  const hasDetectedSpeechRef = useRef<boolean>(false);
  const maxRmsRef = useRef<number>(0);
  const maxVolumeDbRef = useRef<number>(-100);
  const volumeCheckCountRef = useRef<number>(0);
  const recordedAudioBytesRef = useRef<number>(0);
  const audioTrackMutedRef = useRef<boolean | null>(null);

  // 録音タイマー
  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      setRecordingTime(0);
    }
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, [isRecording]);

  useEffect(() => {
    if (!isRecording) {
      setIsRecorderReady(false);
    }
  }, [isRecording]);

  const stopSilenceDetection = () => {
    if (silenceDetectionTimerRef.current) {
      clearInterval(silenceDetectionTimerRef.current);
      silenceDetectionTimerRef.current = null;
    }
  };

  const sendAudioRecorderEvent = (
    event: AudioRecorderEventName,
    details: Record<string, unknown> = {},
  ) => {
    const wsClient = sharedRefs.wsClientRef.current;
    const sessionId = sharedRefs.sessionIdRef.current;
    const recognitionClientId = sharedRefs.recognitionClientIdRef.current;
    const currentAudioTrack = audioStreamRef.current?.getAudioTracks()[0];
    audioTrackMutedRef.current = currentAudioTrack?.muted ?? audioTrackMutedRef.current;

    if (!wsClient || !wsClient.isConnected()) {
      // 接続が整う前の失敗（最初のマイクが入らないなど）も残すため、HTTP で送る
      postClientEvent({
        event: 'audio_recorder_event',
        source: 'audio_recording',
        sessionId: sessionId ?? undefined,
        details: {
          event,
          recognitionClientId,
          wsConnected: false,
          ...Object.fromEntries(
            Object.entries(details).filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value)),
          ),
        },
      });
      return;
    }

    wsClient.send({
      type: 'audio_recorder_event',
      payload: {
        sessionId,
        recognitionClientId,
        event,
        timestamp: new Date().toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        ...details,
      },
    });
  };

  const startSilenceDetection = () => {
    if (silenceDetectionTimerRef.current) clearInterval(silenceDetectionTimerRef.current);

    const analyser = analyserNodeRef.current;
    if (!analyser) {
      debugWarn('[Recording] AnalyserNodeが設定されていません');
      return;
    }

    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    lastSoundTimeRef.current = performance.now();

    debugLog('[Recording] 無音検知を開始しました', {
      isRecordingRef: isRecordingRef.current,
      isStoppingRef: isStoppingRef.current,
      hasAnalyser: !!analyserNodeRef.current,
      bufferLength,
      threshold: silenceThreshold,
      duration: SILENCE_DURATION,
      interval: VOLUME_CHECK_INTERVAL
    });

    let checkCount = 0;
    silenceDetectionTimerRef.current = setInterval(() => {
      checkCount++;

      if (isStoppingRef.current || !isRecordingRef.current) {
        if (isStoppingRef.current) {
          debugLog(`[Recording] 停止処理中なので無音検知を停止します (checkCount=${checkCount})`);
        } else {
          debugLog(`[Recording] 録音中ではないので無音検知を停止します (checkCount=${checkCount}, isRecordingRef=${isRecordingRef.current})`);
        }
        stopSilenceDetection();
        return;
      }

      const currentAnalyser = analyserNodeRef.current;
      if (!currentAnalyser) {
        debugLog('[Recording] AnalyserNodeが存在しないので無音検知を停止します');
        stopSilenceDetection();
        return;
      }

      currentAnalyser.getByteTimeDomainData(dataArray);

      let sum = 0;
      let maxSample = 0;
      for (let i = 0; i < bufferLength; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        const absNormalized = Math.abs(normalized);
        if (absNormalized > maxSample) maxSample = absNormalized;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / bufferLength);
      const volumeDb = rms > 0.001 ? 20 * Math.log10(rms) : -100;
      volumeCheckCountRef.current = checkCount;
      maxRmsRef.current = Math.max(maxRmsRef.current, rms);
      maxVolumeDbRef.current = Math.max(maxVolumeDbRef.current, volumeDb);

      const now = performance.now();
      const silenceDuration = now - lastSoundTimeRef.current;

      const isAboveRmsThreshold = rms > RMS_THRESHOLD;
      const isAboveDbThreshold = volumeDb > silenceThreshold;
      const isSoundDetected = isAboveRmsThreshold && isAboveDbThreshold;

      if (checkCount <= 5 || checkCount % 10 === 0) {
        const lastSoundTimeAgo = now - lastSoundTimeRef.current;
        debugLog(`[Recording] 無音検知チェック #${checkCount}: RMS=${rms.toFixed(4)} (閾値=${RMS_THRESHOLD}, 超過=${isAboveRmsThreshold}), 音量=${volumeDb.toFixed(2)}dB (閾値=${silenceThreshold}dB, 超過=${isAboveDbThreshold}), 最大サンプル=${maxSample.toFixed(4)}, 無音継続時間=${silenceDuration}ms, 音声検知=${isSoundDetected ? 'YES' : 'NO'}, 最後の音声から=${lastSoundTimeAgo}ms前`);
      }

      if (isSoundDetected) {
        hasDetectedSpeechRef.current = true;
        const previousLastSoundTime = lastSoundTimeRef.current;
        const previousSilenceDuration = now - previousLastSoundTime;
        lastSoundTimeRef.current = now;

        if (checkCount <= 5 || checkCount % 10 === 0) {
          debugLog(`[Recording] 🔊 音声検知 #${checkCount}: RMS=${rms.toFixed(4)} > ${RMS_THRESHOLD}, 音量=${volumeDb.toFixed(2)}dB > ${silenceThreshold}dB, 前回無音時間=${previousSilenceDuration}ms, lastSoundTime更新: ${previousLastSoundTime} -> ${lastSoundTimeRef.current} (${previousSilenceDuration}ms無音後)`);
        }
      } else {
        if (silenceDuration >= SILENCE_DURATION) {
          if (!isRecordingRef.current || isStoppingRef.current) {
            debugLog(`[Recording] 🔇 無音検知: 録音状態が既に変更されています。自動停止をスキップします。isRecordingRef=${isRecordingRef.current}, isStopping=${isStoppingRef.current}`);
            stopSilenceDetection();
            return;
          }
          debugLog(`[Recording] 🔇 無音検知: ${silenceDuration}ms無音が続きました。自動停止します。RMS=${rms.toFixed(4)}, 音量=${volumeDb.toFixed(2)}dB, チェック回数=${checkCount}, 最後の音声検知から=${silenceDuration}ms前`);
          stopSilenceDetection();
          handleStopRecording('silence');
          return;
        } else {
          if (checkCount <= 5 || checkCount % 10 === 0) {
            const remaining = SILENCE_DURATION - silenceDuration;
            debugLog(`[Recording] 🔇 無音継続中 #${checkCount}: ${silenceDuration}ms / ${SILENCE_DURATION}ms, RMS=${rms.toFixed(4)}, 音量=${volumeDb.toFixed(2)}dB, 残り${remaining}msで停止, 最後の音声検知から=${silenceDuration}ms前`);
          }
        }
      }
    }, VOLUME_CHECK_INTERVAL);
  };

  const resetRecordingAfterSessionMissing = async () => {
    recordingStartGenerationRef.current += 1;

    if (recordingStartTimeoutRef.current) {
      clearTimeout(recordingStartTimeoutRef.current);
      recordingStartTimeoutRef.current = null;
    }

    stopSilenceDetection();
    isStoppingRef.current = true;

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.stop();
      } catch (stopError) {
        debugWarn('[Recording] MediaRecorder stop during STT session reset failed', stopError);
      }
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        await audioContextRef.current.close();
      } catch (audioContextError) {
        debugWarn('[Recording] AudioContext close during STT session reset failed', audioContextError);
      }
      audioContextRef.current = null;
    }

    sendAudioRecorderEvent('cancelled', {
      stage: 'stt_session_missing',
      reason: 'session_missing',
    });

    analyserNodeRef.current = null;
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    recognitionStartedRef.current = false;
    isRecordingRef.current = false;
    isStoppingRef.current = false;
    callbacks.setIsAwaitingResponse(false);
    setIsRecording(false);
    setIsRecorderReady(false);
    setRecordingTime(0);
  };

  const handleStopRecording = async (reason: AudioStopReason = 'user') => {
    if (isStoppingRef.current || !isRecordingRef.current) {
      debugLog(`[Recording] 既に停止処理中または録音中ではありません。reason=${reason}, isStopping=${isStoppingRef.current}, isRecordingRef=${isRecordingRef.current}, isRecording=${isRecording}`);
      return;
    }

    isStoppingRef.current = true;
    recordingStartGenerationRef.current += 1;
    debugLog(`[Recording] 録音停止開始: reason=${reason}`);

    if (recordingStartTimeoutRef.current) {
      clearTimeout(recordingStartTimeoutRef.current);
      recordingStartTimeoutRef.current = null;
    }

    const wasPreparing = !isRecorderReady && !recognitionStartedRef.current;
    if (wasPreparing) {
      sendAudioRecorderEvent('cancelled', { stage: 'preparing', reason });
    }

    stopSilenceDetection();

    isRecordingRef.current = false;
    setIsRecording(false);
    setIsRecorderReady(false);

    const wsClient = sharedRefs.wsClientRef.current;
    const sessionId = sharedRefs.sessionIdRef.current;
    const recognitionClientId = sharedRefs.recognitionClientIdRef.current;
    const audioMetricsAvailable = volumeCheckCountRef.current > 0;
    const speechDetected = hasDetectedSpeechRef.current;
    const discardSilentAudio = audioMetricsAvailable && !speechDetected;
    const stopMetrics = {
      reason,
      speechDetected,
      audioMetricsAvailable,
      maxRms: Number(maxRmsRef.current.toFixed(6)),
      maxVolumeDb: Number(maxVolumeDbRef.current.toFixed(2)),
      volumeCheckCount: volumeCheckCountRef.current,
      audioBytes: recordedAudioBytesRef.current,
      audioChunks: audioChunksRef.current.length,
      trackMuted: audioTrackMutedRef.current,
    };

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (wsClient && wsClient.isConnected() && sessionId && recognitionStartedRef.current) {
      const hasSentAudio = audioChunksRef.current.length > 0;
      wsClient.send({
        type: 'recognition_stop',
        payload: {
          sessionId,
          recognitionClientId,
          ...stopMetrics,
        },
      });
      sendAudioRecorderEvent(
        discardSilentAudio ? 'silent_audio_rejected' : 'recording_stopped',
        stopMetrics,
      );

      if (hasSentAudio && !discardSilentAudio) {
        callbacks.setIsAwaitingResponse(true);
      }
      // 音声未送信なら応答を待たない
    }

    // マイク/AudioContext はターンごとに閉じない。面接中マイクを常駐させて iOS の音声セッションを
    // 維持し、応答動画の unmute が pause されない（＝映像/音声が止まらない）ようにするため。
    // 実際の解放（track停止・ctx close）は面接終了/ページ離脱時に useInterviewWebSocket の
    // クリーンアップでまとめて行う。
    if (analyserNodeRef.current) {
      try { analyserNodeRef.current.disconnect(); } catch { /* noop */ }
    }
    analyserNodeRef.current = null;
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    recognitionStartedRef.current = false;
    isStoppingRef.current = false;

    if (discardSilentAudio) {
      callbacks.toast({
        title: "マイクの音声を確認できませんでした",
        description: "入力するマイクを確認して、もう一度マイクを押して話してください。",
        variant: "destructive",
      });
    } else if (reason === 'silence') {
      callbacks.toast({
        title: "録音を自動停止しました",
        description: "無音が検知されたため、録音を終了しました。",
      });
    }

    debugLog(`[Recording] 録音停止完了: reason=${reason}`);
  };

  const handleStartRecording = async () => {
    if (isRecording || isRecordingRef.current || isStoppingRef.current) {
      debugLog(`[Recording] 既に録音中または停止処理中です。isRecording=${isRecording}, isRecordingRef=${isRecordingRef.current}, isStopping=${isStoppingRef.current}`);
      return;
    }

    const wsClient = sharedRefs.wsClientRef.current;
    if (!wsClient) {
      callbacks.toast({
        title: "通信を準備しています",
        description: "少し待ってから、もう一度マイクを押してください。",
        variant: "destructive",
      });
      return;
    }

    // 再接続待ちの間に二重でマイク押下されても多重処理しないよう、録音意図を先に確保する。
    isRecordingRef.current = true;
    const startGeneration = recordingStartGenerationRef.current + 1;
    recordingStartGenerationRef.current = startGeneration;
    const isCurrentStart = () =>
      recordingStartGenerationRef.current === startGeneration
      && isRecordingRef.current
      && !isStoppingRef.current;

    const sessionId = sharedRefs.sessionIdRef.current;
    if (!sessionId) {
      isRecordingRef.current = false;
      callbacks.toast({
        title: "面接を準備しています",
        description: "少し待ってから、もう一度マイクを押してください。",
        variant: "destructive",
      });
      return;
    }
    const recognitionClientId = `${sessionId}:${Date.now()}:${startGeneration}`;
    sharedRefs.recognitionClientIdRef.current = recognitionClientId;

    // 通信断後にマイクを押した場合、自動再接続が尽きている/進行中だと未接続のままになる。
    // ここは録音開始前に同期する。ローカル録音を前倒しするのは recognition_start 後だけに限定する。
    if (!wsClient.isConnected()) {
      callbacks.toast({
        title: "再接続しています",
        description: "通信を復旧しています。少しお待ちください…",
      });
      sendAudioRecorderEvent('ws_connect_start', {
        stage: 'ws_connect_start',
        elapsedMs: 0,
        recognitionClientId,
      });
      const wsConnectStartedAt = Date.now();
      const reconnected = await wsClient.ensureConnected();
      if (!isCurrentStart()) {
        debugLog('[Recording] 再接続待ち中に新しい録音開始/リセットが入ったため中止します。');
        return;
      }
      if (!reconnected || !wsClient.isConnected()) {
        sendAudioRecorderEvent('ws_connect_failed', {
          stage: 'ws_connect_failed',
          elapsedMs: Date.now() - wsConnectStartedAt,
          recognitionClientId,
        });
        isRecordingRef.current = false;
        callbacks.toast({
          title: "通信を復旧できませんでした",
          description: "通信を復旧できませんでした。ページを再読み込みしてから、もう一度お試しください。",
          variant: "destructive",
        });
        return;
      }
      sendAudioRecorderEvent('ws_connect_success', {
        stage: 'ws_connect_success',
        elapsedMs: Date.now() - wsConnectStartedAt,
        recognitionClientId,
      });
    }

    if (!wsClient.isSessionReady()) {
      callbacks.toast({
        title: "再接続しています",
        description: "面接セッションを復旧しています。少しお待ちください…",
      });
      sendAudioRecorderEvent('session_ready_start', {
        stage: 'session_ready_start',
        elapsedMs: 0,
        recognitionClientId,
      });
      const sessionReadyStartedAt = Date.now();
      const sessionReady = await wsClient.ensureSessionReady();
      if (!isCurrentStart()) {
        debugLog('[Recording] 面接セッション復旧待ち中に新しい録音開始/リセットが入ったため中止します。');
        return;
      }
      if (!sessionReady || !wsClient.isSessionReady()) {
        sendAudioRecorderEvent('session_ready_failed', {
          stage: 'session_ready_failed',
          elapsedMs: Date.now() - sessionReadyStartedAt,
          recognitionClientId,
        });
        isRecordingRef.current = false;
        callbacks.toast({
          title: "面接を復旧できませんでした",
          description: "面接セッションを復旧できませんでした。ページを再読み込みしてから、もう一度お試しください。",
          variant: "destructive",
        });
        return;
      }
      sendAudioRecorderEvent('session_ready_success', {
        stage: 'session_ready_success',
        elapsedMs: Date.now() - sessionReadyStartedAt,
        recognitionClientId,
      });
    }

    isStoppingRef.current = false;
    isRecordingRef.current = true;
    setIsRecording(true);
    setIsRecorderReady(false);
    callbacks.toast({
      title: "マイク準備中",
      description: "紙飛行機アイコンに変わったら話し始めてください。",
    });

    sharedRefs.sttSessionMessageIdRef.current = null;
    sharedRefs.sttPreviousInterimTextRef.current = '';
    sharedRefs.sttConfirmedTextRef.current = '';
    let recognitionStarted = false;
    let startTimedOut = false;
    let pendingStream: MediaStream | null = null;
    let prepareStage: AudioRecorderEventName = 'prepare_start';
    const startedAt = Date.now();

    const emitPrepareEvent = (
      event: AudioRecorderEventName,
      details: Record<string, unknown> = {},
    ) => {
      prepareStage = event;
      sendAudioRecorderEvent(event, {
        stage: prepareStage,
        elapsedMs: Date.now() - startedAt,
        ...details,
      });
    };

    const clearStartTimeout = () => {
      if (recordingStartTimeoutRef.current) {
        clearTimeout(recordingStartTimeoutRef.current);
        recordingStartTimeoutRef.current = null;
      }
    };

    const resetAfterPrepareFailure = async (options: { sendRecognitionStop?: boolean } = {}) => {
      stopSilenceDetection();

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (stopError) {
          debugWarn('[Recording] MediaRecorder stop during prepare cleanup failed', stopError);
        }
      }

      const streamToStop = pendingStream ?? audioStreamRef.current;
      if (streamToStop) {
        streamToStop.getTracks().forEach(track => track.stop());
      }

      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try {
          await audioContextRef.current.close();
        } catch (audioContextError) {
          debugWarn('[Recording] AudioContext close during prepare cleanup failed', audioContextError);
        }
      }

      if (options.sendRecognitionStop !== false && recognitionStarted && wsClient.isConnected()) {
        wsClient.send({ type: 'recognition_stop', payload: { sessionId, recognitionClientId } });
      }

      audioStreamRef.current = null;
      analyserNodeRef.current = null;
      mediaRecorderRef.current = null;
      audioContextRef.current = null;
      audioChunksRef.current = [];
      recognitionStartedRef.current = false;
      isRecordingRef.current = false;
      isStoppingRef.current = false;
      setIsRecording(false);
      setIsRecorderReady(false);
    };

    emitPrepareEvent('prepare_start');

    // マイク準備のタイムアウトは、マイクを使える状態になってから計る。
    // 初回はブラウザの許可ダイアログが出るため、許可を押すまでの時間まで含めると、
    // 許可のあとに届いたマイクを捨てて「タイムアウト」になり、初回だけ録音できないことがある。
    const armStartTimeout = () => {
      clearStartTimeout();
      recordingStartTimeoutRef.current = setTimeout(() => {
        if (recordingStartGenerationRef.current !== startGeneration || !isRecordingRef.current || isRecorderReady) return;

        startTimedOut = true;
        recordingStartTimeoutRef.current = null;
        emitPrepareEvent('timeout', { stage: prepareStage, timeoutMs: RECORDING_START_TIMEOUT_MS });
        void resetAfterPrepareFailure();
        callbacks.toast({
          title: "マイク準備がタイムアウトしました",
          description: "もう一度マイクボタンをタップしてください。",
          variant: "destructive",
        });
      }, RECORDING_START_TIMEOUT_MS);
    };

    try {
      // 既存のマイクストリームが生きていれば再利用する（面接中はマイクを常駐させ、ターン間で
      // stream/AudioContext を閉じない＝iOS の音声セッションを維持する）。
      const existingStream = audioStreamRef.current;
      const existingStreamLive = !!existingStream
        && existingStream.getAudioTracks().some(track => track.readyState === 'live');

      let stream: MediaStream;
      if (existingStreamLive && existingStream) {
        stream = existingStream;
      } else {
        emitPrepareEvent('get_user_media_start');
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            noiseSuppression: true,
            echoCancellation: true,
            autoGainControl: true,
          },
        });
        pendingStream = stream;

        // getUserMedia中にstopが呼ばれた場合、リソースを解放して終了
        if (startTimedOut || !isCurrentStart()) {
          debugLog('[Recording] 初期化中に停止が要求されました。リソースを解放します。');
          stream.getTracks().forEach(track => track.stop());
          isStoppingRef.current = false;
          return;
        }

      }

      audioStreamRef.current = stream;
      armStartTimeout();
      const audioTrack = stream.getAudioTracks()[0];
      audioTrackMutedRef.current = audioTrack?.muted ?? null;
      emitPrepareEvent('get_user_media_success', {
        audioTracks: stream.getAudioTracks().length,
        trackMuted: audioTrack?.muted ?? null,
        trackEnabled: audioTrack?.enabled ?? null,
        trackReadyState: audioTrack?.readyState ?? null,
        trackLabel: audioTrack?.label || undefined,
        reusedStream: existingStreamLive,
      });

      if (!isCurrentStart()) {
        debugLog('[Recording] MediaRecorder作成前に古い録音開始処理になったため中止します。');
        if (pendingStream === stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        if (pendingStream === stream && audioStreamRef.current === stream) {
          audioStreamRef.current = null;
        }
        return;
      }

      emitPrepareEvent('media_recorder_create_start');
      const {
        mediaRecorder,
        requestedMimeType,
        actualMimeType,
        usedBrowserDefaultFallback,
      } = createMediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      hasDetectedSpeechRef.current = false;
      maxRmsRef.current = 0;
      maxVolumeDbRef.current = -100;
      volumeCheckCountRef.current = 0;
      recordedAudioBytesRef.current = 0;
      emitPrepareEvent('media_recorder_created', {
        audioMimeType: actualMimeType,
        requestedAudioMimeType: requestedMimeType,
        usedBrowserDefaultFallback,
      });

      let recognitionReadyForAudio = false;
      let bufferedAudioBytes = 0;
      const preReadyAudioBuffers: ArrayBuffer[] = [];

      const flushPreReadyAudioBuffers = () => {
        if (!wsClient.isConnected() || !isCurrentStart()) return;
        const bufferedChunks = preReadyAudioBuffers.splice(0);
        for (const buffer of bufferedChunks) {
          wsClient.sendBinary(buffer);
        }
        emitPrepareEvent('recognition_ready', {
          audioMimeType: actualMimeType,
          requestedAudioMimeType: requestedMimeType,
          usedBrowserDefaultFallback,
          bufferedChunks: bufferedChunks.length,
          bufferedBytes: bufferedAudioBytes,
        });
        bufferedAudioBytes = 0;
      };

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size <= 0) return;

        audioChunksRef.current.push(event.data);
        recordedAudioBytesRef.current += event.data.size;
        event.data.arrayBuffer().then(buffer => {
          if (!isCurrentStart()) return;

          if (recognitionReadyForAudio) {
            if (wsClient.isConnected() && sharedRefs.sttReadyRef.current) {
              wsClient.sendBinary(buffer);
            }
            return;
          }

          bufferedAudioBytes += buffer.byteLength;
          preReadyAudioBuffers.push(buffer);
          if (
            bufferedAudioBytes > PRE_READY_AUDIO_BUFFER_MAX_BYTES
            || preReadyAudioBuffers.length > PRE_READY_AUDIO_BUFFER_MAX_CHUNKS
          ) {
            debugWarn('[Recording] recognition_ready前の音声バッファが上限を超えました。録音準備を中止します。', {
              bufferedAudioBytes,
              bufferedChunks: preReadyAudioBuffers.length,
            });
            emitPrepareEvent('error', {
              stage: 'media_recorder_start',
              errorName: 'PreReadyAudioBufferLimitExceeded',
              errorMessage: 'recognition_ready前の音声バッファが上限を超えました',
              bufferedAudioBytes,
              bufferedChunks: preReadyAudioBuffers.length,
            });
            void resetAfterPrepareFailure();
          }
        });
      };

      mediaRecorder.onstop = () => {};

      // 再度チェック: start前にstopが呼ばれてないか
      if (startTimedOut || !isCurrentStart()) {
        debugLog('[Recording] MediaRecorder.start前に停止が要求されました。クリーンアップします。');
        stream.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
        analyserNodeRef.current = null;
        mediaRecorderRef.current = null;
        recognitionStartedRef.current = false;
        isStoppingRef.current = false;
        return;
      }

      emitPrepareEvent('media_recorder_start', {
        bufferingUntilRecognitionReady: true,
      });
      mediaRecorder.start(MEDIA_RECORDER_TIMESLICE_MS);

      const waitForRecognitionReady = () => new Promise<boolean>((resolve) => {
        let timeout: number;
        const handler = (message: { payload?: any }) => {
          const readySessionId = message.payload?.sessionId;
          if (typeof readySessionId === 'string' && readySessionId !== sessionId) {
            return;
          }
          const readyRecognitionClientId = message.payload?.recognitionClientId;
          if (typeof readyRecognitionClientId === 'string' && readyRecognitionClientId !== recognitionClientId) {
            return;
          }
          window.clearTimeout(timeout);
          wsClient.off('recognition_ready', handler);
          resolve(true);
        };

        wsClient.on('recognition_ready', handler);
        timeout = window.setTimeout(() => {
          wsClient.off('recognition_ready', handler);
          resolve(false);
        }, RECOGNITION_READY_TIMEOUT_MS);
      });

      wsClient.send({
        type: 'recognition_start',
        payload: {
          sessionId,
          recognitionClientId,
          language: 'ja-JP',
          audioMimeType: actualMimeType,
          requestedAudioMimeType: requestedMimeType,
          usedBrowserDefaultFallback,
          userAgent: navigator.userAgent,
        },
      });
      recognitionStarted = true;
      recognitionStartedRef.current = true;
      // G(STT readyゲート): この録音は今まさに recognition_start を送った最新セッション。
      // 直前の切断/セッション喪失でゲートが閉じていても、新しい録音の音声（先頭の WebM ヘッダ含む）は
      // 送る必要があるため、ここでゲートを開ける。古い録音の漏れチャンクだけが閉じている間に弾かれる。
      sharedRefs.sttReadyRef.current = true;
      emitPrepareEvent('recognition_start_sent', {
        audioMimeType: actualMimeType,
        requestedAudioMimeType: requestedMimeType,
        usedBrowserDefaultFallback,
        recognitionClientId,
      });
      emitPrepareEvent('recognition_ready_wait_start', {
        audioMimeType: actualMimeType,
        requestedAudioMimeType: requestedMimeType,
        usedBrowserDefaultFallback,
        recognitionClientId,
      });
      const recognitionReady = await waitForRecognitionReady();
      if (!recognitionReady || startTimedOut || !isCurrentStart()) {
        emitPrepareEvent('recognition_ready_failed', {
          stage: 'recognition_start_sent',
          timeoutMs: RECOGNITION_READY_TIMEOUT_MS,
          recognitionReady,
          recognitionClientId,
        });
        emitPrepareEvent('timeout', {
          stage: 'recognition_start_sent',
          timeoutMs: RECOGNITION_READY_TIMEOUT_MS,
          recognitionReady,
        });
        const shouldToast = !startTimedOut && isCurrentStart();
        await resetAfterPrepareFailure();
        if (shouldToast) {
          callbacks.toast({
            title: "マイク準備がタイムアウトしました",
            description: "通信を確認して、もう一度マイクボタンをタップしてください。",
            variant: "destructive",
          });
        }
        return;
      }
      recognitionReadyForAudio = true;
      flushPreReadyAudioBuffers();
      debugLog('[Recording] MediaRecorder MIME selected', {
        requestedMimeType,
        actualMimeType,
        usedBrowserDefaultFallback,
      });
      clearStartTimeout();
      setIsRecorderReady(true);
      emitPrepareEvent('ready', {
        audioMimeType: actualMimeType,
        requestedAudioMimeType: requestedMimeType,
        usedBrowserDefaultFallback,
      });

      try {
        // AudioContext もターン間で再利用する（面接中ずっと running を維持）。
        let audioCtx = audioContextRef.current;
        if (!audioCtx || audioCtx.state === 'closed') {
          audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
          audioContextRef.current = audioCtx;
        }
        if (audioCtx.state === 'suspended') {
          void audioCtx.resume();
        }

        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);
        analyserNodeRef.current = analyser;
        // iOS の音声セッションを「入力＋出力あり」で維持するため、無音(gain 0)で destination にも接続。
        // これにより応答動画の unmute が iOS に pause されなくなる。
        const keepAliveGain = audioCtx.createGain();
        keepAliveGain.gain.value = 0;
        source.connect(keepAliveGain).connect(audioCtx.destination);
        startSilenceDetection();
      } catch (audioContextError) {
        debugWarn('[Recording] 無音検知の初期化に失敗しました。手動送信は継続します。', audioContextError);
        emitPrepareEvent('audio_context_error', errorToPayload(audioContextError));
      }
    } catch (err) {
      if (!isCurrentStart()) {
        if (pendingStream) {
          pendingStream.getTracks().forEach(track => track.stop());
        }
        return;
      }
      clearStartTimeout();
      if (startTimedOut) {
        return;
      }
      emitPrepareEvent('error', { stage: prepareStage, ...errorToPayload(err) });
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      const streamToStop = pendingStream ?? audioStreamRef.current;
      if (streamToStop) {
        streamToStop.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        await audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserNodeRef.current = null;
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      if (recognitionStarted && wsClient.isConnected()) {
        wsClient.send({ type: 'recognition_stop', payload: { sessionId, recognitionClientId } });
      }
      recognitionStartedRef.current = false;
      callbacks.toast({ ...toUserFacingError(err, "microphone"), variant: "destructive" });
      isRecordingRef.current = false;
      setIsRecording(false);
      setIsRecorderReady(false);
      isStoppingRef.current = false;
    }
  };

  const handleSendAudio = async () => {
    if (!isRecording || isStoppingRef.current) {
      debugLog('handleSendAudio: 重複実行防止', { isRecording, isStopping: isStoppingRef.current });
      return;
    }
    debugLog('handleSendAudio: ユーザー操作による録音停止');
    await handleStopRecording('user');
  };

  return {
    isRecording,
    isRecorderReady,
    recordingTime,
    isRecordingRef,
    isStoppingRef,
    audioStreamRef,
    audioContextRef,
    handleStartRecording,
    handleStopRecording,
    handleSendAudio,
    resetRecordingAfterSessionMissing,
    stopSilenceDetection,
  };
}
